"use client";
import { Vehicle } from "@alba-cars/common-modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Navigation, Pagination, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import IconArrowDown from "@/components/Icons/IconArrowDown";
import CarsCard from "@/components/ui/CarsCard";
import SectionTitle from "@/components/ui/SectionTitle";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Link } from "@/i18n/routing";

interface NewCarsSwiperProps {
  cars: Vehicle[];
  direction?: "horizontal" | "vertical";
  title?: string;
  showHeader?: boolean;
}

const NewCarsSwiper = ({
  cars,
  direction = "vertical",
  title = "Our Latest Cars",
  showHeader = true,
}: NewCarsSwiperProps) => {
  const locale = useLocale();
  const t = useTranslations("Articles");
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  const handleNextSlide = () => {
    if (!swiperRef.current) return;

    if (activeIndex === cars.length - 1) {
      swiperRef.current.slideTo(0);
    } else {
      swiperRef.current.slideNext();
    }
  };

  if (!cars || cars.length === 0) {
    return null;
  }

  // Horizontal version
  if (direction === "horizontal") {
    return (
      <section className="mt-16">
        {showHeader && (
          <SectionTitle
            title={title}
            buttonNavigation={"/buy-used-cars-uae?sort%5BcreatedAt%5D=desc"}
            buttonText={t("article.newCars.seeAll")}
            locale={locale}
            showIcon={true}
          />
        )}

        <div className="relative">
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Navigation, Pagination]}
            navigation={{
              nextEl: ".horizontal-new-cars-next",
              prevEl: ".horizontal-new-cars-prev",
            }}
            slidesPerView={1}
            spaceBetween={16}
            breakpoints={{
              640: {
                slidesPerView: 2,
                spaceBetween: 16,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 20,
              },
              1280: {
                slidesPerView: 4,
                spaceBetween: 24,
              },
            }}
            className="pb-12"
          >
            {cars.map((vehicle: Vehicle) => (
              <SwiperSlide key={vehicle.id}>
                <CarsCard car={vehicle} useSlider={false} />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Navigation Buttons */}
          <button
            className="horizontal-new-cars-prev absolute -left-4 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white p-3 shadow-lg transition-all duration-300 disabled:opacity-50 lg:flex"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <button
            className="horizontal-new-cars-next absolute -right-4 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white p-3 shadow-lg transition-all duration-300 disabled:opacity-50 lg:flex"
            aria-label="Next slide"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Pagination */}
          <div className="horizontal-new-cars-pagination mt-4 flex justify-center gap-2"></div>
        </div>
      </section>
    );
  }

  // Vertical version (original)
  return (
    <div className="">
      {showHeader && (
        <div className="mb-6 flex items-center justify-between px-2">
          <h3 className="urbanist text-xl font-semibold">{title}</h3>
          <Link
            href={
              locale === "en"
                ? "/buy-used-cars-uae?sort%5BcreatedAt%5D=desc"
                : `/${locale}/buy-used-cars-uae?sort%5BcreatedAt%5D=desc`
            }
            className="inter font-semibold text-primary transition-all duration-300 hover:text-primary-dark hover:underline"
          >
            {t("article.newCars.seeAll")}
          </Link>
        </div>
      )}
      <div className="relative">
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          modules={[Navigation, Virtual]}
          navigation={{
            nextEl: ".vertical-new-cars-next",
            prevEl: ".vertical-new-cars-prev",
          }}
          preventInteractionOnTransition={true}
          virtual={{
            addSlidesAfter: 1,
            addSlidesBefore: 1,
            enabled: true,
          }}
          slidesPerView={1}
          spaceBetween={16}
          direction="vertical"
          className="h-[450px]"
          autoplay
        >
          {cars.map((vehicle: Vehicle) => (
            <SwiperSlide key={vehicle.id}>
              <CarsCard car={vehicle} />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="flex justify-end">
          <button
            onClick={handleNextSlide}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleNextSlide();
              }
            }}
            className="flex w-full items-center justify-center bg-white text-center text-white transition-all duration-300"
            aria-label="Next slide"
            tabIndex={0}
          >
            <span className="flex animate-bounce items-center gap-2 text-primary">
              <IconArrowDown fill="#ff3347" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewCarsSwiper;
